import axios from 'axios';
import {
  AuthHookEnum,
  AuthorizationProcedures,
  getIdentityProviderHook,
  validateChosenHook,
  getUserFromOidcClient,
} from './utils';
import { Auth0ContextInterface, User as Auth0User } from '@auth0/auth0-react';
import { AuthContextProps } from 'react-oidc-context';

/*
 * Return the correct enum based on the set environment variable for the
 * desired identity provider
 */
export const getIdentityProvider = (): AuthHookEnum => {
  const envvar = import.meta.env.VITE_IDENTITY_PROVIDER || '';
  if (envvar == 'AUTH0' || envvar == '') {
    return AuthHookEnum.Auth0;
  } else if (envvar == 'AUTHENTIK') {
    return AuthHookEnum.Authentik;
  } else {
    throw new Error(`Identity provider incorrectly set: ${envvar}`);
  }
};

/*
 *
 * Sets the function defined by the AuthorizationProcedures interface.
 *
 * Depending on if Auth0 or the OIDC Authentication flow is chosen, a
 * slightly different function is created for the specific identity provider.
 *
 *
 */

export const useAuthenticationApi = (): AuthorizationProcedures => {
  const authEnum = getIdentityProvider();
  const oidcAuthentication = getIdentityProviderHook(authEnum);
  const oidcAuth = oidcAuthentication();

  validateChosenHook(authEnum, oidcAuth);

  switch (authEnum) {
    /*
     *
     * Authentication using the remote identity provider Auth0.
     *
     */
    case AuthHookEnum.Auth0: {
      const oidcAuth0 = oidcAuth as Auth0ContextInterface<Auth0User>; // satisfy typescript
      const getSessionIdAuth0 = () => {
        const sessionId = localStorage.getItem('sessionId');
        if (sessionId) {
          return new Promise((resolve) => {
            resolve({ 'session-id': sessionId });
          });
        } else {
          return oidcAuth0
            .getAccessTokenSilently({
              authorizationParams: {
                audience: import.meta.env.VITE_AUTH0_AUDIENCE!,
              },
            })
            .then((token) => {
              return {
                Authorization: `Bearer ${token}`,
              };
            })
            .then((headers) => {
              return axios
                .post('sessions/create_session', {}, { headers })

                .then((response) => {
                  localStorage.setItem('sessionId', response.data);
                  return { 'session-id': `${response.data}` };
                });
            })
            .catch((error) => {
              if (error.response?.status === 404) {
                oidcAuth0.logout({ logoutParams: { returnTo: window.location.origin } });
              }
            });
        }
      };

      const logoutAuth0 = (identityProviderLogOut: boolean) => {
        return getSessionIdAuth0()
          .catch((_error) => {
            if (identityProviderLogOut) {
              oidcAuth0.logout({ logoutParams: { returnTo: window.location.origin } });
            }
          })
          .then((headers: any) => {
            return axios
              .get(`/sessions/log_out`, { headers })

              .then(() => {
                localStorage.removeItem('sessionId');
                if (identityProviderLogOut) {
                  oidcAuth0.logout({ logoutParams: { returnTo: window.location.origin } });
                }
              });
          });
      };
      return {
        getSessionId: getSessionIdAuth0,
        logout: logoutAuth0,
      };
    }

    /*
     *
     * Authentication using the local identity provider Authentik.
     *
     */
    case AuthHookEnum.Authentik: {
      const auth = oidcAuth as AuthContextProps; // satisfy typescript

      const getSessionIdAuthentik = () => {
        const sessionId = localStorage.getItem('sessionId');
        if (sessionId) {
          return new Promise((resolve) => {
            resolve({ 'session-id': sessionId });
          });
        } else {
          return getUserFromOidcClient(auth.settings.authority, auth.settings.client_id)
            .then((user) => {
              return {
                Authorization: `Bearer ${user?.access_token}`,
              };
            })
            .then((headers) => {
              return axios.post('sessions/create_session', {}, { headers }).then((response) => {
                localStorage.setItem('sessionId', response.data);
                return { 'session-id': `${response.data}` };
              });
            })
            .catch((error) => {
              if (error.response?.status === 404) {
                auth.signoutRedirect();
              }
            });
        }
      };
      const logoutAuthentik = (identityproviderLogOut: boolean) => {
        return getSessionIdAuthentik()
          .catch((_error) => {
            if (identityproviderLogOut) {
              auth.signoutRedirect();
            }
          })
          .then((headers: any) => {
            return axios.get(`/sessions/log_out`, { headers }).then(() => {
              localStorage.removeItem('sessionId');
              if (identityproviderLogOut) {
                auth.signoutRedirect();
              }
            });
          });
      };
      return {
        getSessionId: getSessionIdAuthentik,
        logout: logoutAuthentik,
      };
    }
    default: {
      throw new Error('Identity provider not selected');
    }
  }
};
